import { gql } from '@apollo/client';
import { CARRIER_INVOICE_FIELDS } from '@/graphql/queries/carrier_invoices';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { CLAIM_ADJUSTMENT_FIELDS } from '@/graphql/fragments/claim_adjustments';
import { SUPPLEMENTAL_CHARGE_FIELDS } from '@/graphql/fragments/supplemental_charges';
import { ORDER_REFERENCE_FIELDS } from '@/graphql/fragments/order_references';
import { INTEGRATION_OUTBOUND_FIELDS } from '@/graphql/fragments/integrations_outbound';
import { PARTNERSHIP_FIELDS } from '@/graphql/fragments/partnerships';
import { INVOICE_EXPORT_ORDER_FIELDS } from '@/graphql/fragments/invoice_export_orders';

const RECEIVABLE_FIELDS = gql`
    ${CARRIER_INVOICE_FIELDS}
    ${CLAIM_ADJUSTMENT_FIELDS}
    ${SUPPLEMENTAL_CHARGE_FIELDS}
    ${ORDER_REFERENCE_FIELDS}
    ${INTEGRATION_OUTBOUND_FIELDS}
    ${PARTNERSHIP_FIELDS}
    ${INVOICE_EXPORT_ORDER_FIELDS}

    fragment ReceivableFields on carrier_invoices {
        ...CarrierInvoiceFields
        netsuite_account_id
        netsuite_invoice_id

        outbound_integrations(
            where: { transaction_type: { _eq: "210_MOTOR_CARRIER_FREIGHT_DETAILS_AND_INVOICE" } }
            order_by: { status: desc }
            limit: 1
        ) {
            ...IntegrationOutboundFields
        }

        supplemental_charges {
            ...SupplementalChargeFields
        }

        claim_adjustments {
            ...ClaimAdjustmentFields
            claim {
                claim_id
                order {
                    order_id
                    order_number
                    po_number
                    reference_id
                    order_shipper {
                        client_id
                        business_name
                    }
                    ...InvoiceExportOrderFields
                }
            }
        }

        client {
            client_id
            business_name
            scac
            email
            billing_email
            summarized_billing
            user {
                user_id
                circles
            }
            shipper_partnerships {
                ...PartnershipFields
            }
        }
        partner_client {
            client_id
            business_name
        }

        shipper_orders {
            order_id
            order_number
            po_number
            reference_id
            pod
            delivery_photo
            customer_signature
            delivery_date
            # Origin Plant ID (origin_id) and Channel (channel_code) for the MasterBrand export, read from
            # references first and custom_fields second. The full references fragment rather than
            # key/value: order_references has no cache key, so a narrower list would replace the one the
            # order pages cache on the same order.
            custom_fields
            references {
                ...OrderReferenceFields
            }
            received_date
            est_received_date
            # Cabinet vs. piece counts for the MasterBrand export. intangible excludes the lines
            # pricing strips before rating, so the quantities match the charges.
            itemsByOrderId {
                item_id
                quantity
                item_type_details
                intangible
            }
            ...InvoiceExportOrderFields
            pickup_street_address
            pickup_unit
            pickup_city
            pickup_state
            pickup_zip
            dropoff_name
            dropoff_street
            dropoff_unit
            dropoff_city
            dropoff_state
            dropoff_zip
            shipper_rate
            middle_mile_shipper_rate
            warehouse_shipper_rate
            # Gates the middle-mile leg in the MasterBrand export, matching how the EDI 210 gates it.
            middle_mile_carrier_id
            price_breakdown
            qbo_tag
            invoice_class
            billing_completion_time

            shipper_rate_override {
                pricing_override_id
                marketplace_tier
            }

            order_shipper {
                client_id
                business_name
            }

            order_carrier {
                client_id
                business_name
                site_code
            }

            outbound_integrations(
                where: { transaction_type: { _eq: "210_MOTOR_CARRIER_FREIGHT_DETAILS_AND_INVOICE" } }
                order_by: { status: desc }
                limit: 1
            ) {
                ...IntegrationOutboundFields
            }
        }
    }
`;

export const RECEIVABLES = gql`
    ${RECEIVABLE_FIELDS}
    query GetReceivables($where: carrier_invoices_bool_exp!, $limit: Int!, $offset: Int!) {
        results: carrier_invoices(where: $where, order_by: { created_at: desc }, limit: $limit, offset: $offset) {
            ...ReceivableFields
        }
    }
`;

export const GET_SHIPPERS = gql`
    ${CLIENT_FIELDS}
    query GetShippers($where: carrier_invoices_bool_exp!) {
        results: carrier_invoices(distinct_on: [client_id], where: $where) {
            carrier_invoice_id
            client_id
            client {
                ...ClientFields
            }
        }
    }
`;

export const UPDATE_INVOICES = gql`
    ${CARRIER_INVOICE_FIELDS}
    mutation UpdateInvoices($ids: [uuid!]!, $patch: carrier_invoices_set_input!) {
        updated: update_carrier_invoices(where: { carrier_invoice_id: { _in: $ids } }, _set: $patch) {
            returning {
                ...CarrierInvoiceFields
            }
        }
    }
`;

export const CREATE_INVOICES = gql`
    ${RECEIVABLE_FIELDS}
    mutation CreateInvoices($invoices: [carrier_invoices_insert_input!]!) {
        created: insert_carrier_invoices(objects: $invoices) {
            returning {
                ...ReceivableFields
            }
        }
    }
`;
